import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, MapPin, ChevronLeft, ChevronRight, Check, Home, Building2, Sofa, Sparkles } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import { useAuth } from '@/contexts/AuthContext';
import { BOOKING } from '@/constants/testIds';
import SEO from '@/components/dubinko/SEO';

const STEPS = ['Usluga', 'Detalji', 'Termin', 'Kontakt', 'Potvrda'];

const SERVICE_META = {
  residential: { label: 'Stan / kuća', icon: Home, testid: BOOKING.serviceResidential, color: 'emerald' },
  commercial:  { label: 'Poslovni prostor', icon: Building2, testid: BOOKING.serviceCommercial, color: 'blue' },
  carpet:      { label: 'Tepisi & tapacirung', icon: Sofa, testid: BOOKING.serviceCarpet, color: 'purple' },
};

export default function BookingPage() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    service_type: params.get('service') || 'residential',
    rooms: 2,
    property_size: '60',
    preferred_date: '',
    preferred_time: '10:00',
    address: '',
    notes: '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    promo_code: '',
  });
  const [promoInfo, setPromoInfo] = useState(null); // { valid, percent, code } | null
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoMsg, setPromoMsg] = useState('');

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        guest_name: user.name || f.guest_name,
        guest_email: user.email || f.guest_email,
        guest_phone: user.phone || f.guest_phone,
      }));
    }
  }, [user]);

  const minDate = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const estPrice = useMemo(() => {
    if (form.service_type === 'carpet') {
      const m2 = parseInt(form.property_size || '0', 10) || 0;
      return Math.max(5 * Math.max(m2, 10), 50);
    }
    const base = form.service_type === 'commercial' ? 30 : 25;
    return base * Math.max(form.rooms || 1, 1) + 30;
  }, [form]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canProceed = () => {
    if (step === 0) return !!form.service_type;
    if (step === 1) {
      if (form.service_type === 'carpet') return parseInt(form.property_size, 10) > 0;
      return (form.rooms || 0) > 0;
    }
    if (step === 2) return !!form.preferred_date && !!form.preferred_time;
    if (step === 3) {
      if (user) return !!form.address;
      return !!form.address && !!form.guest_name && !!form.guest_email && !!form.guest_phone;
    }
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        service_type: form.service_type,
        property_size: form.service_type === 'carpet' ? `${form.property_size}m2` : `${form.rooms} sobe`,
        rooms: form.service_type === 'carpet' ? null : Number(form.rooms),
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time,
        address: form.address,
        city: 'Niš',
        notes: form.notes,
        promo_code: promoInfo?.valid ? promoInfo.code : null,
        guest_name: user ? null : form.guest_name,
        guest_email: user ? null : form.guest_email,
        guest_phone: user ? null : form.guest_phone,
      };
      const { data } = await api.post('/bookings', payload);
      trackEvent('booking_completed', { service: form.service_type, value: Number(data.estimated_price || 0) });
      // Fire marketing conversion events (only if pixels are configured on the page)
      try {
        const price = Number(data.estimated_price || 0);
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', { event_category: 'booking', value: price, currency: 'EUR' });
          window.gtag('event', 'booking_completed', { value: price, currency: 'EUR' });
        }
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', { value: price, currency: 'EUR' });
          window.fbq('track', 'Schedule');
        }
      } catch (_) {}
      setSuccess(data);
      setStep(STEPS.length);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      <SEO
        title="Zakaži dubinsko čišćenje online"
        description="Online rezervacija dubinskog čišćenja u Nišu u 5 jednostavnih koraka. Bez obaveza."
        path="/rezervacija"
      />
      <section className="section-pad aurora-bg grain">
        <div className="container-x">
          <span className="tag-pill">Online rezervacija</span>
          <h1 className="font-display text-5xl md:text-6xl font-extrabold mt-4 max-w-3xl">
            Zakaži termin u 5 koraka
          </h1>
          <p className="text-slate-600 text-lg mt-4 max-w-xl">
            Brzo, jednostavno i bez obaveze. Plaćate samo nakon završenog posla i vašeg odobrenja.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x max-w-3xl">
          {/* Progress */}
          <div className="flex items-center justify-between mb-8">
            {STEPS.map((label, i) => (
              <div key={label} className="flex-1 flex items-center">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                  i < step ? 'bg-emerald-600 text-white' : i === step ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${i < step ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="text-center text-sm font-semibold text-slate-500 uppercase tracking-widest mb-6">
            {step < STEPS.length ? `Korak ${step + 1} od ${STEPS.length} · ${STEPS[step]}` : 'Završeno'}
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 p-6 md:p-10 shadow-sm" data-testid={BOOKING.wizard}>
            {success ? (
              <div data-testid={BOOKING.success} className="text-center py-6">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <Check className="w-8 h-8" />
                </div>
                <h2 className="font-display text-3xl font-bold mt-4">Rezervacija primljena!</h2>
                <p className="text-slate-600 mt-2">Hvala, <strong>{success.customer_name}</strong>. Pozvaćemo vas u najkraćem roku na <strong>{success.customer_phone || success.customer_email}</strong> radi potvrde.</p>
                <div className="mt-6 inline-flex flex-col gap-2 bg-slate-50 rounded-2xl p-5 text-left">
                  <div><span className="text-slate-500 text-sm">Usluga:</span> <strong>{SERVICE_META[success.service_type]?.label}</strong></div>
                  <div><span className="text-slate-500 text-sm">Termin:</span> <strong>{success.preferred_date} u {success.preferred_time}</strong></div>
                  <div><span className="text-slate-500 text-sm">Adresa:</span> <strong>{success.address}, {success.city}</strong></div>
                  {success.estimated_price && (
                    <div><span className="text-slate-500 text-sm">Procena cene:</span> <strong>{success.estimated_price.toFixed(0)} €</strong></div>
                  )}
                </div>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <Link to="/" className="btn-ghost px-5 py-3 rounded-full font-semibold">Nazad na početnu</Link>
                  {user ? (
                    <Link to="/nalog" className="btn-primary px-5 py-3 rounded-full font-semibold">Moje rezervacije</Link>
                  ) : (
                    <Link to="/registracija" className="btn-primary px-5 py-3 rounded-full font-semibold">Napravi nalog</Link>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* STEP 0 — service */}
                {step === 0 && (
                  <div data-testid={BOOKING.step1}>
                    <h2 className="font-display text-2xl font-bold">Koju uslugu želite?</h2>
                    <p className="text-slate-600 mt-1">Odaberite jednu od tri kategorije.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                      {Object.entries(SERVICE_META).map(([key, meta]) => {
                        const Icon = meta.icon;
                        const active = form.service_type === key;
                        return (
                          <button
                            key={key}
                            data-testid={meta.testid}
                            onClick={() => update('service_type', key)}
                            type="button"
                            className={`text-left p-5 rounded-2xl border-2 transition ${active ? 'border-blue-700 bg-blue-50' : 'border-slate-200 hover:border-slate-400'}`}
                          >
                            <Icon className={`w-6 h-6 ${active ? 'text-blue-700' : 'text-slate-500'}`} />
                            <div className="font-display font-bold mt-2">{meta.label}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* STEP 1 — details */}
                {step === 1 && (
                  <div data-testid={BOOKING.step2}>
                    <h2 className="font-display text-2xl font-bold">Detalji prostora</h2>
                    {form.service_type === 'carpet' ? (
                      <div className="mt-6">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Procenjena površina (m²)</label>
                        <input
                          data-testid={BOOKING.size}
                          type="number" min="5" inputMode="numeric"
                          value={form.property_size}
                          onChange={(e) => update('property_size', e.target.value)}
                          className="w-full px-4 py-3 rounded-2xl border border-slate-300 focus:border-blue-700 outline-none"
                        />
                        <p className="text-sm text-slate-500 mt-2">Cena se računa po m². Minimum naplate 50 €.</p>
                      </div>
                    ) : (
                      <div className="mt-6">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Broj prostorija</label>
                        <input
                          data-testid={BOOKING.rooms}
                          type="number" min="1" max="20" inputMode="numeric"
                          value={form.rooms}
                          onChange={(e) => update('rooms', Number(e.target.value))}
                          className="w-full px-4 py-3 rounded-2xl border border-slate-300 focus:border-blue-700 outline-none"
                        />
                        <p className="text-sm text-slate-500 mt-2">Uračunavamo i kuhinju, kupatilo i hodnik.</p>
                      </div>
                    )}
                    <div className="mt-6 bg-slate-50 rounded-2xl p-4 flex items-center justify-between">
                      <div className="text-sm text-slate-600">Procena cene</div>
                      <div className="font-display font-bold text-2xl text-slate-900">~ {estPrice.toFixed(0)} €</div>
                    </div>
                  </div>
                )}

                {/* STEP 2 — date/time */}
                {step === 2 && (
                  <div data-testid={BOOKING.step3}>
                    <h2 className="font-display text-2xl font-bold">Termin</h2>
                    <p className="text-slate-600 mt-1">Izaberite datum i vreme dolaska.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2"><Calendar className="w-4 h-4 inline mr-1" /> Datum</label>
                        <input
                          data-testid={BOOKING.date}
                          type="date" min={minDate}
                          value={form.preferred_date}
                          onChange={(e) => update('preferred_date', e.target.value)}
                          className="w-full px-4 py-3 rounded-2xl border border-slate-300 focus:border-blue-700 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2"><Clock className="w-4 h-4 inline mr-1" /> Vreme</label>
                        <select
                          data-testid={BOOKING.time}
                          value={form.preferred_time}
                          onChange={(e) => update('preferred_time', e.target.value)}
                          className="w-full px-4 py-3 rounded-2xl border border-slate-300 focus:border-blue-700 outline-none bg-white"
                        >
                          {['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00'].map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 3 — contact */}
                {step === 3 && (
                  <div data-testid={BOOKING.step4}>
                    <h2 className="font-display text-2xl font-bold">Kontakt i adresa</h2>
                    <div className="mt-6 space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2"><MapPin className="w-4 h-4 inline mr-1" /> Adresa u Nišu</label>
                        <input
                          data-testid={BOOKING.address}
                          value={form.address}
                          onChange={(e) => update('address', e.target.value)}
                          placeholder="Npr. Obrenovićeva 25, Niš"
                          className="w-full px-4 py-3 rounded-2xl border border-slate-300 focus:border-blue-700 outline-none"
                        />
                      </div>
                      {!user && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Ime i prezime</label>
                            <input data-testid={BOOKING.guestName} value={form.guest_name} onChange={(e) => update('guest_name', e.target.value)}
                              className="w-full px-4 py-3 rounded-2xl border border-slate-300 outline-none focus:border-blue-700" />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Telefon</label>
                            <input data-testid={BOOKING.guestPhone} value={form.guest_phone} onChange={(e) => update('guest_phone', e.target.value)}
                              placeholder="+381 ..." className="w-full px-4 py-3 rounded-2xl border border-slate-300 outline-none focus:border-blue-700" />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
                            <input data-testid={BOOKING.guestEmail} type="email" value={form.guest_email} onChange={(e) => update('guest_email', e.target.value)}
                              className="w-full px-4 py-3 rounded-2xl border border-slate-300 outline-none focus:border-blue-700" />
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Napomena (opciono)</label>
                        <textarea
                          data-testid={BOOKING.notes}
                          value={form.notes}
                          onChange={(e) => update('notes', e.target.value)}
                          rows={3}
                          placeholder="Specifični zahtevi, pristupne informacije…"
                          className="w-full px-4 py-3 rounded-2xl border border-slate-300 focus:border-blue-700 outline-none resize-none"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 4 — confirm */}
                {step === 4 && (
                  <div data-testid={BOOKING.step5}>
                    <h2 className="font-display text-2xl font-bold">Pregled i potvrda</h2>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <Row label="Usluga" value={SERVICE_META[form.service_type]?.label} />
                      <Row label={form.service_type === 'carpet' ? 'Površina' : 'Broj prostorija'} value={form.service_type === 'carpet' ? `${form.property_size} m²` : `${form.rooms}`} />
                      <Row label="Datum" value={form.preferred_date} />
                      <Row label="Vreme" value={form.preferred_time} />
                      <Row label="Adresa" value={form.address} />
                      <Row label="Procena" value={`${estPrice.toFixed(0)} €`} />
                      {!user && <>
                        <Row label="Kupac" value={form.guest_name} />
                        <Row label="Email" value={form.guest_email} />
                        <Row label="Telefon" value={form.guest_phone} />
                      </>}
                    </div>
                    {form.notes && (
                      <div className="mt-4 p-4 bg-slate-50 rounded-2xl text-sm text-slate-700"><strong>Napomena:</strong> {form.notes}</div>
                    )}

                    <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl">
                      <label className="block text-sm font-semibold text-slate-700 mb-2">🎁 Imate promo kod?</label>
                      <div className="flex gap-2">
                        <input
                          data-testid="promo-code-input"
                          value={form.promo_code}
                          onChange={(e) => { update('promo_code', e.target.value.toUpperCase()); setPromoInfo(null); setPromoMsg(''); }}
                          placeholder="PRVI15"
                          className="flex-1 px-4 py-2.5 rounded-full border border-emerald-300 outline-none focus:border-emerald-600 uppercase" />
                        <button
                          type="button"
                          data-testid="promo-apply-btn"
                          onClick={applyPromo}
                          disabled={promoBusy || !form.promo_code.trim()}
                          className="btn-green px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60">
                          {promoBusy ? '…' : 'Primeni'}
                        </button>
                      </div>
                      {promoMsg && (
                        <div className={`mt-2 text-xs font-semibold ${promoInfo?.valid ? 'text-emerald-700' : 'text-red-700'}`}>
                          {promoMsg}
                        </div>
                      )}
                    </div>

                    <p className="text-xs text-slate-500 mt-6 flex items-start gap-2">
                      <Sparkles className="w-3.5 h-3.5 mt-0.5 text-purple-600" />
                      Cena je orijentaciona. Konačan iznos potvrđujemo telefonom pre dolaska.
                    </p>
                  </div>
                )}

                {error && (
                  <div className="mt-4 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm">{error}</div>
                )}

                <div className="flex items-center justify-between mt-8">
                  <button
                    data-testid={BOOKING.back}
                    disabled={step === 0}
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    className="btn-ghost px-5 py-2.5 rounded-full font-semibold disabled:opacity-40 inline-flex items-center gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" /> Nazad
                  </button>
                  {step < STEPS.length - 1 ? (
                    <button
                      data-testid={BOOKING.next}
                      disabled={!canProceed()}
                      onClick={() => { if (step === 0) trackEvent('booking_started', { service: form.service_type }); setStep((s) => s + 1); }}
                      className="btn-primary px-6 py-2.5 rounded-full font-semibold disabled:opacity-40 inline-flex items-center gap-2"
                    >
                      Dalje <ChevronRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      data-testid={BOOKING.submit}
                      disabled={submitting}
                      onClick={submit}
                      className="btn-green px-6 py-2.5 rounded-full font-semibold disabled:opacity-50 inline-flex items-center gap-2"
                    >
                      {submitting ? 'Šaljemo…' : 'Potvrdi rezervaciju'} <Check className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex flex-col bg-slate-50 rounded-2xl p-3.5">
      <span className="text-xs uppercase tracking-widest text-slate-500">{label}</span>
      <span className="font-semibold text-slate-900 mt-1">{value || '—'}</span>
    </div>
  );
}
